// Development fixture for the Temp tab (`?mock=temp`) and the shared rrd-file generator used by the Vent
// mock. A winter day anchored at the current time (the season is pinned to mid-January for "today", so a
// year back shows a full seasonal curve): Väljas ≈ −3…−6 °C at ≈ 90 % RH, Kuur ≈ 0 °C, rooms 19.5–22 °C
// at RH 30–40 %, uHoo history with one room's CO2 elevated (≈ 1060 ppm) now. Kuur has a 40-minute gap
// yesterday and the newest row of the fine files is null, as in real exports. The curves are the
// prototypes' (docs/temp-vent-tabs-redesign/prototype-temp-*.html), plus outdoor humidity.
import { EnergyAirQuality, EnergyAirQualityRoom } from '../../common';
import { HISTORY_RANGES, HistoryRange } from './history';
import { RrdFile } from './rrd';
import { localMidnight } from './tallinn_time';

const H = 3_600_000;
const SLOT_MS = 15 * 60_000;

// export.sh's step per range (seconds), and the uHoo history's (export_history.ts).
export const RRD_STEP_S: Record<HistoryRange, number> = {
  '3h': 60,
  '24h': 300,
  '48h': 900,
  '1w': 1800,
  '1m': 7200,
  '3m': 14400,
  '1y': 43200,
};
// (No uHoo export for 3m / 1y.)
export const UHOO_STEP_S: Partial<Record<HistoryRange, number>> = {
  '3h': 900,
  '24h': 900,
  '48h': 900,
  '1w': 1800,
  '1m': 7200,
};

// ---- Curves: t = hours since today's local midnight ----------------------------------------------------

const doy = (t: number) => 13 + t / 24;
const yearCos = (t: number) => Math.cos((2 * Math.PI * (doy(t) - 20)) / 365);
const season = (t: number) => 4.5 - 11.5 * yearCos(t);
const amp = (t: number) => 3.2 - 1.8 * yearCos(t);
export const hod = (t: number) => ((t % 24) + 24) % 24;
const wx = (t: number) => 2.6 * Math.sin(t / 29) + 1.8 * Math.sin(t / 71 + 1) + 1.1 * Math.sin(t / 11 + 2);
const warm = (t: number) => Math.max(0, season(t) - 14);
const daily = (t: number, peakHour: number) => Math.sin((2 * Math.PI * (hod(t) - peakHour)) / 24);

export const outdoor = (t: number) => season(t) + wx(t) + amp(t) * daily(t, 9);
// Living room (also the vent mock's room temperature).
export const tuba = (t: number) => 21.6 + 0.45 * daily(t, 16) + 0.2 * Math.sin(t / 13) + warm(t) * 0.45;

const TEMP: Record<string, (t: number) => number> = {
  outside: outdoor,
  kuur: (t) => season(t) * 0.85 + 3.4 + 0.6 * wx(t - 5) + 0.35 * amp(t) * daily(t, 12),
  tuba,
  Kabinet: (t) => {
    const h = hod(t);
    return (
      21 + (h >= 9 && h < 18 ? 0.6 * Math.sin(((h - 9) / 9) * Math.PI) : 0) + 0.15 * Math.sin(t / 7) + warm(t) * 0.5
    );
  },
  Magamistuba: (t) => 19.7 + 0.35 * daily(t, 23) + 0.15 * Math.sin(t / 9 + 1) + warm(t) * 0.5,
};

const rhBase = (t: number) => 42 - 10 * yearCos(t) + 2.5 * Math.sin(t / 17);
const RH: Record<string, (t: number) => number> = {
  // Outdoors: ≈ 85–98 % in winter, lower in summer, dipping in the afternoon.
  outside: (t) => Math.min(99, 78 + 12 * yearCos(t) - 6 * daily(t, 15) + 3 * Math.sin(t / 23)),
  tuba: (t) => rhBase(t) + 1.5 * daily(t, 19),
  Kabinet: (t) => rhBase(t) - 2.2 + Math.sin(t / 5),
  Magamistuba: (t) => rhBase(t) + 4 + 2 * daily(t, 6),
};

// CO2 anchored at "now" (nowT), so whatever the clock: the study ≈ 820 ppm, the bedroom ≈ 1060 (Elevated).
function dailyBump(t: number, peakT: number, base: number, height: number, widthH: number): number {
  const d = ((((t - peakT) % 24) + 36) % 24) - 12;
  return base + height * Math.exp(-(d * d) / (2 * widthH * widthH));
}
const co2Curve = (room: string, nowT: number) =>
  room === 'Kabinet'
    ? (t: number) => dailyBump(t, nowT - 2, 520, 375, 3)
    : (t: number) => dailyBump(t, nowT - 1, 600, 500, 2.5);

// ---- rrd files -----------------------------------------------------------------------------------------

export type Curve = (t: number) => number | null;

// An rrdtool-xport-shaped file over the range ending now: row ts = the mean of the curve over the step
// ending at ts (sampled 6 times for steps of an hour and more).
export function mockRrd(
  legends: string[],
  curves: Curve[],
  range: HistoryRange,
  now: number,
  opts: { stepS?: number; lastNull?: boolean } = {},
): RrdFile {
  const stepS = opts.stepS ?? RRD_STEP_S[range];
  const stepMs = stepS * 1000;
  const midnight = localMidnight(now);
  const end = Math.floor(now / stepMs) * stepMs;
  const start = end - HISTORY_RANGES.find((r) => r.key === range)!.hours * H;
  const at = (curve: Curve, ts: number): number | null => {
    if (stepMs < H) {
      return curve((ts - midnight) / H);
    }
    let sum = 0;
    for (let i = 0; i < 6; i++) {
      const v = curve((ts - stepMs + ((i + 0.5) * stepMs) / 6 - midnight) / H);
      if (v === null) {
        return null;
      }
      sum += v;
    }
    return sum / 6;
  };
  const data: (number | null)[][] = [];
  for (let ts = start + stepMs; ts <= end; ts += stepMs) {
    const last = ts === end && opts.lastNull && stepS <= 300;
    data.push([
      ts / 1000,
      ...curves.map((c) => {
        const v = last ? null : at(c, ts);
        return v === null ? null : Math.round(v * 100) / 100;
      }),
    ]);
  }
  return { meta: { legend: legends, start: start / 1000, end: end / 1000, step: stepS }, data };
}

const RANGE_SUFFIX = /(3h|24h|48h|1w|1m|3m|1y)$/;

export const rangeOf = (name: string) => (name.match(RANGE_SUFFIX)?.[1] as HistoryRange | undefined) ?? undefined;

// The Temp tab's files: {outside,kuur,tuba}_temperature<range>, uhoo_<range>. `legacy`: what older exports
// have (no humidity column for Väljas and Tuba, no uHoo history).
export function tempMockFile(name: string, now: number, legacy: boolean): RrdFile | undefined {
  const range = rangeOf(name);
  if (!range) {
    return undefined;
  }
  const sensor = name.slice(0, name.indexOf('_'));
  const nowT = (now - localMidnight(now)) / H;
  if (name.startsWith('uhoo_')) {
    if (legacy || !UHOO_STEP_S[range]) {
      return undefined;
    }
    const legends: string[] = [];
    const curves: Curve[] = [];
    for (const room of ['Kabinet', 'Magamistuba']) {
      legends.push(`${room} temp`, `${room} RH`, `${room} CO2`);
      curves.push(TEMP[room], RH[room], co2Curve(room, nowT));
    }
    return mockRrd(legends, curves, range, now, { stepS: UHOO_STEP_S[range] });
  }
  if (!['outside', 'kuur', 'tuba'].includes(sensor) || !name.includes('_temperature')) {
    return undefined;
  }
  const temp = TEMP[sensor];
  // Kuur: the sensor dropped out for 40 minutes yesterday afternoon.
  const curve: Curve = sensor === 'kuur' ? (t) => (t > nowT - 20 && t < nowT - 19.33 ? null : temp(t)) : temp;
  if (RH[sensor] && !legacy) {
    return mockRrd(['Temperatuur', 'Õhuniiskus'], [curve, RH[sensor]], range, now, { lastNull: true });
  }
  return mockRrd(['Temperatuur'], [curve], range, now, { lastNull: true });
}

// The uHoo part of the plan snapshot: latest readings and the last 24 h of 15-minute CO2 means.
export function mockAirQuality(now: number): EnergyAirQuality {
  const uhoo = tempMockFile('uhoo_24h', now, false)!;
  const midnight = localMidnight(now);
  const rooms: EnergyAirQualityRoom[] = ['Kabinet', 'Magamistuba'].map((name, r) => {
    const col = 1 + r * 3;
    const slots: EnergyAirQualityRoom['slots'] = uhoo.data.map((row) => {
      const co2 = Math.round(row[col + 2] as number);
      return { start: (row[0] as number) * 1000 - SLOT_MS, co2, co2Max: co2 + 25 };
    });
    const nowT = (now - midnight) / H;
    const t = nowT - 2 / 60;
    return {
      name,
      latest: {
        timestamp: now - 2 * 60_000,
        co2: Math.round(co2Curve(name, nowT)(t)),
        temperatureC: Math.round(TEMP[name](t) * 10) / 10,
        humidity: Math.round(RH[name](t)),
        pm25: 4,
        tvoc: r === 0 ? 35 : 80,
      },
      slots,
    };
  });
  return { thresholds: { good: 1000, high: 1200 }, rooms };
}
